import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SidePanel } from '@/admin-area/ui/SidePanel';
import { AddressField } from '@/components/address/address-field';
import { isAddressEmpty, type AddressValue } from '@/components/address/address-value';
import { FormFooter } from '@/components/ui/form-footer';
import { Input } from '@/components/ui/input';
import { countryOptions } from '@/profile/countries';
import { addressOf, addressUpdateOf, addressValueOf, sameAddress } from '../address';
import { MacroCategoriesField, type SupplierMacro } from '../types';
import {
  addName,
  addSupplierSite,
  type Supplier,
  type SupplierPatch,
  updateSite,
  updateSupplier,
  type WriteOutcome,
} from './api';
import { CountrySelect } from './CountrySelect';
import { SupplierLink } from './panel-links';

type Form = {
  name: string;
  legalName: string;
  country: string;
  vat: string;
  fiscalCode: string;
  phone: string;
  usualCurrency: string;
  foreignTaxRegime: string;
  macroCategories: readonly SupplierMacro[];
};

function formOf(supplier: Supplier): Form {
  const vat = supplier.vat
    ? [
        supplier.vatCountry && supplier.vatCountry !== supplier.country ? supplier.vatCountry : '',
        supplier.vat,
      ]
        .filter(Boolean)
        .join(' ')
    : '';
  return {
    name: supplier.name,
    legalName: supplier.legalName ?? '',
    country: supplier.country ?? '',
    vat,
    fiscalCode: supplier.fiscalCode ?? '',
    phone: supplier.phone ?? '',
    usualCurrency: supplier.usualCurrency ?? '',
    foreignTaxRegime: supplier.foreignTaxRegime ?? '',
    macroCategories: supplier.macroCategories,
  };
}

const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

/**
 * What changed, as the PATCH carries it; nothing else is sent. A VAT number with its own prefix
 * («DE 123456789») sends the prefix as `vatCountry`; without one the API takes the country's.
 */
export function patchOf(before: Form, after: Form): SupplierPatch {
  const patch: SupplierPatch = {};
  if (after.name.trim() !== before.name.trim()) patch.name = after.name.trim();
  if (after.legalName !== before.legalName) patch.legalName = orNull(after.legalName);
  if (after.country !== before.country) patch.country = orNull(after.country);
  if (after.vat.trim() !== before.vat.trim()) {
    const compact = after.vat.replace(/[\s.-]/g, '').toUpperCase();
    // The Swiss IDI keeps its «CHE»: it is the number, not a country prefix.
    const prefix = /^CHE\d/.test(compact)
      ? null
      : (/^([A-Z]{2})(?=[0-9A-Z]{2,})/.exec(compact)?.[1] ?? null);
    patch.vat = compact === '' ? null : prefix ? compact.slice(2) : compact;
    patch.vatCountry = compact === '' ? null : prefix;
  }
  if (after.fiscalCode !== before.fiscalCode) {
    patch.fiscalCode = orNull(after.fiscalCode.toUpperCase());
  }
  if (after.phone !== before.phone) patch.phone = orNull(after.phone);
  if (after.usualCurrency !== before.usualCurrency) {
    patch.usualCurrency = orNull(after.usualCurrency.toUpperCase());
  }
  if (after.foreignTaxRegime !== before.foreignTaxRegime) {
    patch.foreignTaxRegime = orNull(after.foreignTaxRegime);
  }
  if (after.macroCategories.join() !== before.macroCategories.join()) {
    patch.macroCategories = [...after.macroCategories];
  }
  return patch;
}

type Failure = { readonly text: string; readonly existing: { id: string; name: string } | null };

/**
 * The registered office's write, after the registry's: its address changed on the site it is, or a
 * new site made the registered office when there was none. Nothing when the field says what was
 * saved, or when there was no office and the field is empty.
 */
export function officeWrite(
  supplier: Supplier,
  office: AddressValue,
): (() => Promise<WriteOutcome>) | null {
  const current = supplier.registeredOffice;
  if (sameAddress(office, current?.address, supplier.country)) return null;
  if (current) {
    const address = addressUpdateOf(office, current.address);
    return () => updateSite(supplier.id, current.id, { address });
  }
  const address = addressOf(office);
  if (address === null) return null;
  return () => addSupplierSite(supplier.id, { address, registeredOffice: true });
}

/**
 * «Modifica» (board 3.1, «Completa» on board 3.3): the registry fields and the registered office
 * (the shared address component, board 2), for whoever the API lets edit them (`can.edit`). The
 * registry is sent with `If-Match`; a change made meanwhile reloads the page. The registered office
 * is a site (`updateSupplierSite`, or `addSupplierSite` with `registeredOffice` when there is none),
 * written after the registry; the other sites are changed from «Sedi».
 */
export function EditSupplierDialog({
  open,
  onOpenChange,
  supplier,
  onDone,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly supplier: Supplier;
  readonly onDone: (outcome: WriteOutcome, done: string) => void;
}) {
  const { t, i18n } = useTranslation('suppliers');
  const formId = useId();
  const before = useMemo(() => formOf(supplier), [supplier]);
  const [form, setForm] = useState<Form>(before);
  const [alias, setAlias] = useState('');
  const officeBefore = useMemo(
    () => addressValueOf(supplier.registeredOffice?.address, supplier.country),
    [supplier],
  );
  const [office, setOffice] = useState<AddressValue>(officeBefore);
  const [tried, setTried] = useState(false);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const countries = useMemo(
    () => [
      { value: '', label: t('edit.noCountry') },
      ...countryOptions(i18n.resolvedLanguage ?? 'it').map((option) => ({
        value: option.code,
        label: option.name,
      })),
    ],
    [i18n.resolvedLanguage, t],
  );

  useEffect(() => {
    if (open) {
      setForm(before);
      setOffice(officeBefore);
      setAlias('');
      setTried(false);
      setFailure(null);
    }
    // Only on opening: a supplier replaced while the panel is open must not wipe what was typed.
  }, [open]);

  const nameMissing = form.name.trim() === '';
  const set = (key: Exclude<keyof Form, 'macroCategories'>) => (value: string) =>
    setForm((now) => ({ ...now, [key]: value }));
  const fromSites = [
    ...new Set(
      [supplier.registeredOffice, ...supplier.sites]
        .filter((site) => site !== null && site !== undefined && site.active)
        .flatMap((site) => (site?.macro ? [site.macro] : [])),
    ),
  ];
  // Stable, so the memoized «Paese» does not render its countries again on every key (#986).
  const setCountry = useCallback((country: string) => {
    setForm((now) => ({ ...now, country }));
    // An office not written yet follows the supplier's country.
    setOffice((now) =>
      isAddressEmpty(now) && country !== '' ? { ...now, countryCode: country } : now,
    );
  }, []);

  async function submit() {
    setTried(true);
    if (nameMissing || sending) return;
    const patch = patchOf(before, form);
    const name = alias.trim();
    const writeOffice = officeWrite(supplier, office);
    if (Object.keys(patch).length === 0 && name === '' && writeOffice === null) {
      onOpenChange(false);
      return;
    }
    setSending(true);
    setFailure(null);
    let outcome: WriteOutcome = { ok: true, supplier };
    if (Object.keys(patch).length > 0) outcome = await updateSupplier(supplier, patch);
    if (outcome.ok && writeOffice !== null) outcome = await writeOffice();
    if (outcome.ok && name !== '') outcome = await addName(supplier.id, name);
    setSending(false);
    if (outcome.ok || (outcome.status === 409 && outcome.code === 'VERSION_CONFLICT')) {
      onDone(outcome, t('edit.saved'));
      return;
    }
    setFailure(failureOf(outcome, t));
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title={t('edit.title')}
      description={t('edit.description', { name: supplier.name })}
      footer={
        <FormFooter
          className="w-full border-t-0 pt-0"
          form={formId}
          primaryLabel={t('edit.submit')}
          primaryDisabled={sending}
          cancelLabel={t('actions.cancel')}
          onCancel={() => onOpenChange(false)}
        />
      }
    >
      <form
        id={formId}
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Field label={t('edit.name')} required>
          {(id) => (
            <>
              <Input
                id={id}
                value={form.name}
                maxLength={200}
                aria-invalid={tried && nameMissing}
                onChange={(event) => set('name')(event.target.value)}
              />
              {tried && nameMissing ? (
                <span role="alert" className="text-destructive text-sm">
                  {t('edit.nameRequired')}
                </span>
              ) : null}
            </>
          )}
        </Field>
        <TextField
          label={t('data.legalName')}
          value={form.legalName}
          max={200}
          onChange={set('legalName')}
        />
        <MacroCategoriesField
          value={form.macroCategories}
          fromSites={fromSites}
          onChange={(macroCategories) => setForm((now) => ({ ...now, macroCategories }))}
        />
        <Field label={t('data.country')} note={t('edit.optionalMasculine')}>
          {(id) => (
            <CountrySelect id={id} value={form.country} choices={countries} onChange={setCountry} />
          )}
        </Field>
        <TextField
          label={t('data.vat')}
          value={form.vat}
          max={34}
          help={t('edit.vatHelp')}
          onChange={set('vat')}
        />
        {form.country === 'IT' ? (
          <TextField
            label={t('data.fiscalCode')}
            note={t('edit.optionalMasculine')}
            value={form.fiscalCode}
            max={16}
            onChange={set('fiscalCode')}
          />
        ) : null}
        <AddressField
          label={t('data.registeredOffice')}
          labelNote={t('edit.optional')}
          value={office}
          onChange={setOffice}
        />
        <TextField
          label={t('data.phone')}
          note={t('edit.optionalMasculine')}
          value={form.phone}
          max={40}
          type="tel"
          help={t('edit.phoneHelp')}
          onChange={set('phone')}
        />
        <TextField
          label={t('data.usualCurrency')}
          value={form.usualCurrency}
          max={3}
          onChange={set('usualCurrency')}
        />
        {form.country !== '' && form.country !== 'IT' ? (
          <TextField
            label={t('data.foreignTaxRegime')}
            note={t('edit.optionalMasculine')}
            value={form.foreignTaxRegime}
            max={100}
            onChange={set('foreignTaxRegime')}
          />
        ) : null}
        <TextField
          label={t('edit.addName')}
          note={t('edit.optionalMasculine')}
          value={alias}
          max={200}
          help={t('edit.addNameHelp')}
          onChange={setAlias}
        />
        {failure ? (
          <p role="alert" className="text-destructive text-sm">
            {failure.text}
            {failure.existing ? (
              <>
                {' '}
                <SupplierLink
                  supplierId={failure.existing.id}
                  className="text-link font-semibold hover:underline"
                >
                  {t('edit.openExisting', { name: failure.existing.name })}
                </SupplierLink>
              </>
            ) : null}
          </p>
        ) : null}
      </form>
    </SidePanel>
  );
}

type T = ReturnType<typeof useTranslation<'suppliers'>>['t'];

function failureOf(outcome: Extract<WriteOutcome, { ok: false }>, t: T): Failure {
  if (outcome.code === 'SUPPLIER_VAT_EXISTS') {
    return { text: t('edit.vatExists'), existing: outcome.existing };
  }
  if (outcome.code === 'SUPPLIER_MERGED') return { text: t('edit.merged'), existing: null };
  if (outcome.status === 403) return { text: t('edit.forbidden'), existing: null };
  if (outcome.status === 422) {
    return { text: outcome.message ?? t('edit.invalid'), existing: null };
  }
  return { text: t('edit.failed'), existing: null };
}

export function Field({
  label,
  required,
  note,
  help,
  children,
}: {
  readonly label: string;
  readonly required?: boolean;
  /** Beside an optional field's label: «· facoltativa» by default, «· facoltativo» for a masculine one. */
  readonly note?: string;
  readonly help?: string;
  readonly children: (id: string) => ReactNode;
}) {
  const { t } = useTranslation('suppliers');
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-semibold">
        {label}
        {required ? null : (
          <span className="text-muted-foreground font-normal"> {note ?? t('edit.optional')}</span>
        )}
      </label>
      {children(id)}
      {help ? <span className="text-muted-foreground text-xs">{help}</span> : null}
    </div>
  );
}

export function TextField({
  label,
  value,
  max,
  note,
  help,
  type,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly max: number;
  readonly note?: string;
  readonly help?: string;
  readonly type?: 'text' | 'tel';
  readonly onChange: (value: string) => void;
}) {
  return (
    <Field
      label={label}
      {...(note === undefined ? {} : { note })}
      {...(help === undefined ? {} : { help })}
    >
      {(id) => (
        <Input
          id={id}
          type={type ?? 'text'}
          value={value}
          maxLength={max}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}
